import { useState, useEffect, useCallback } from 'react';
import { realtimeService } from '../services/realtimeService';

interface EmailStats {
  totalEmails: number;
  clickThroughRate: number;
  openRate: number;
  uniqueEmails: number;
  totalClicks: number;
  averageClicks: number;
}

interface UseEmailOpensOptions {
  page?: number;
  pageSize?: number;
  email?: string;
  startDate?: string;
  endDate?: string;
  realtimeUpdates?: boolean;
}

export function useEmailOpens({
  page = 1,
  pageSize = 10,
  email,
  startDate,
  endDate,
  realtimeUpdates = false,
}: UseEmailOpensOptions = {}) {
  const [stats, setStats] = useState<EmailStats>({
    totalEmails: 0,
    clickThroughRate: 0,
    openRate: 0,
    uniqueEmails: 0,
    totalClicks: 0,
    averageClicks: 0,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribe: () => void;

    const setupSubscription = async () => {
      try {
        setLoading(true);

        if (realtimeUpdates) {
          unsubscribe = realtimeService.subscribeToUpdates('actv_total', (update) => {
            if (update.type === 'UPDATE' || update.type === 'SYNC_COMPLETE') {
              const data = Array.isArray(update.data) ? update.data : [update.data];
              
              // Pega sempre a última linha adicionada
              const latestData = data.sort((a, b) => 
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
              )[0];
              
              setStats({
                totalEmails: latestData?.total_emails_abertos_total || 0,
                clickThroughRate: latestData?.click_through_rate_total || 0,
                openRate: latestData?.open_rate_total || 0,
                uniqueEmails: latestData?.total_user_emails || 0,
                totalClicks: latestData?.clicked_links_total || 0,
                averageClicks: latestData?.total_user_emails > 0 ? 
                  (latestData?.clicked_links_total || 0) / latestData?.total_user_emails : 0
              });
            }
          });
        }

        // Sincroniza dados iniciais
        await realtimeService.syncData('actv_total');
        
      } catch (err) {
        console.error('Erro ao configurar atualizações em tempo real:', err);
        setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };

    setupSubscription();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [page, pageSize, email, startDate, endDate, realtimeUpdates]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      await realtimeService.syncData('actv_total');
    } catch (err) {
      console.error('Erro ao atualizar dados:', err);
      setError(err instanceof Error ? err.message : 'Erro ao atualizar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    stats,
    totalCount,
    loading,
    error,
    pageCount: Math.ceil(totalCount / pageSize),
    refresh
  };
}